import Constants from '@/Constants';
import ConfirmDialog from '@/components/ConfirmDialog';
import FamilySelectionBox from '@/components/FamilySelectionBox';
import SelectCategoryForm from '@/components/SelectCategoryForm';
import TagsInput from '@/components/TagsInput';
import Upload from '@/components/Upload';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import Category from '@/models/Category';
import { CourseLessonContentTO, CourseType } from '@/models/CourseLessonContentTO';
import { EntityType } from '@/models/EntityTypes';
import { Family } from '@/models/Family';
import { LanguageType } from '@/models/LanguageType';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import {
    createCourseRequest,
    getCourseClear,
    getCourseRequest,
    getScormPackageStatusRequest,
    updateCourseRequest,
    updateScormPackageRequest,
} from '@/store/list/action';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import { CourseForm, courseSchema, formInitialState } from './formConfig';
import './style.scss';

interface ConfirmDialogState {
    scormAlert: string;
    leftButtonTitle: string;
    rightButtonTitle: string;
    rightClick: () => void;
    leftClick: () => void;
}

const CourseInfo: React.FC = () => {
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const { strings, identity, course, success, hasErrorOnUpdateScorm, scormPackageStatus, dynamicAttributes, isAdmin } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            identity: state.identity.data,
            course: state.lists.courseData,
            success: state.lists.success,
            hasErrorOnUpdateScorm: state.lists.hasErrorOnUpdateScorm,
            scormPackageStatus: state.lists.scormPackageStatus,
            dynamicAttributes: state.appOrganization.data.dynamicAttributes,
            isAdmin: state.identity.data.user.admin,
        }),
    );

    const [formState, setFormState] = useState(formInitialState);
    const { mainImage, name, author, description, locale, scormPackageName, categories, tags, families }: CourseForm = formState;
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);
    const [originalCourse, setOriginalCourse] = useState<CourseLessonContentTO>();
    const [onUpload, setOnUpload] = useState(false);
    const [scormDialogClosedByUser, setScormDialogClosedByUser] = useState<boolean>(false);
    const [checkScormStatusInterval, setCheckScormStatusInterval] = useState<NodeJS.Timeout | undefined>(undefined);
    const [currentTrainingOfType, setCurrentTrainingOfType] = useState<CourseType>(course?.type ? course?.type : 'REGULAR');
    const uploadMainImageComponentRef = useRef<{ clearFileInput: Function }>(null);
    const uploadScormImageComponentRef = useRef<{ clearFileInput: Function }>(null);

    useEffect(() => {
        !id && clearForm();
        resetNavigationToSetScreen(EntityType.COURSE, course.organizationId, isAdmin, history);
    }, [id, course?.organizationId, isAdmin]);

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 1,
                nextButtonTitle: strings.NEXT,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => {},
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.PREVIOUS,
                saveButtonTitle: course.id ? strings.SAVE_AND_CLOSE : strings.SAVE_TO_DRAFT,
                totalSteps: 2,
                nextDisabled: formIsValid ? false : true,
                previewDisabled: true,
                blockUi: false,
            }),
        );
    }, [dispatch, identity.user.orgId, identity.user.applicationId, strings._LANGUAGE]);

    useEffect(() => {
        const typeOfTraining = localStorage.getItem(Constants.TYPE_OF_TRAINING);

        if (id) {
            dispatch(getCourseRequest(parseInt(id)));
        } else {
            setCurrentTrainingOfType(typeOfTraining ? (typeOfTraining as CourseType) : 'REGULAR');
        }
    }, [id, course.type]);

    useEffect(() => {
        courseSchema
            .validate(formState)
            .then(() => setFormIsValid(true && !onUpload))
            .catch(() => setFormIsValid(false));
    }, [formState, onUpload]);

    useEffect(() => {
        disableSaveAndNavButtons(!formIsValid);
    }, [formIsValid, strings._LANGUAGE, dispatch]);

    const clearForm = () => {
        dispatch(getCourseClear());
        setFormState(formInitialState);
        uploadMainImageComponentRef?.current?.clearFileInput();
        uploadScormImageComponentRef?.current?.clearFileInput();
    };

    const saveConfirmDialog: ConfirmDialogState = {
        scormAlert: strings.NEW_COURSE_SCORM_ALERT,
        leftButtonTitle: strings.CONFIRM_INSERT_SCORM,
        rightButtonTitle: strings.YES,
        rightClick: () => {
            setShow(false);
            handleSaveNext();
        },
        leftClick: () => {
            setShow(false);
            setOnSavePressed(false);
            setOnNextPressed(false);
            setScormDialogClosedByUser(true);
        },
    };

    const updateScormPackageDialog: ConfirmDialogState = {
        scormAlert: strings.SCORM_SCO_WARNING,
        leftButtonTitle: strings.CANCEL,
        rightButtonTitle: strings.UNDERSTAND.toLocaleUpperCase(),
        rightClick: () => {
            setShow(false);
        },
        leftClick: () => {
            setShow(false);
        },
    };

    const [show, setShow] = useState(false);
    const [confirmDialogState, setConfirmDialogState] = useState(saveConfirmDialog);

    const handleSaveNext = () => {
        localStorage.removeItem(Constants.TYPE_OF_TRAINING);
        disableSaveAndNavButtons(true);

        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.LOADING,
                blockUi: true,
            } as FloatingCourseActionsBarConfiguration),
        );

        if (course.id) {
            const updatedCourse = {
                ...course,
                name,
                description,
                scormPackageName,
                mainImage,
                author,
                categories,
                tags,
                families,
                moduleId: Constants.LMS_MODULE_ID,
            };

            if (!_.isEqual(originalCourse, updatedCourse)) {
                dispatch(updateCourseRequest(updatedCourse));
            } else {
                redirect();
            }
        } else {
            dispatch(
                createCourseRequest({
                    bizId: `flow_${new Date().getTime().toString()}`,
                    name,
                    applicationId: Constants.APP_ID,
                    organizationId: families.length > 0 ? null : identity.user.orgId,
                    description,
                    scormPackageName,
                    type: currentTrainingOfType,
                    status: 'DRAFT',
                    locale: locale as LanguageType,
                    mainImage,
                    author,
                    categories,
                    tags,
                    moduleId: Constants.LMS_MODULE_ID,
                    families,
                } as CourseLessonContentTO),
            );
        }
    };

    useEffect(() => {
        if (onSavePressed || onNextPressed) {
            if (!scormPackageName && !course.id && !scormDialogClosedByUser) {
                setConfirmDialogState(saveConfirmDialog);
                setShow(true);
            } else {
                handleSaveNext();
            }
        }
    }, [onSavePressed, onNextPressed]);

    const handleOnChange = useCallback((fieldName: string, value: string | number | object | null | undefined | Family[] | Category[]) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    }, []);

    useEffect(() => {
        if (course.id) {
            handleOnChange('name', course.name);
            handleOnChange('author', course.author);
            handleOnChange('description', course.description || '');
            handleOnChange('locale', course.locale);
            handleOnChange('mainImage', course.mainImage);
            handleOnChange('scormPackageName', course.scormPackageName);
            handleOnChange('tags', course.tags && course.tags.length > 0 ? course.tags : []);
            handleOnChange('families', course.families && course.families.length > 0 ? course.families : []);
            handleOnChange('categories', course.categories || []);

            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveButtonTitle: strings.SAVE_AND_CLOSE,
                    saveDisabled: false,
                    nextDisabled: false,
                } as FloatingCourseActionsBarConfiguration),
            );

            setOriginalCourse(course);

            if (course.scormPackageName) {
                startCheckScormStatusInterval();
            }
        }
    }, [course]);

    useEffect(() => {
        if (success) {
            redirect();
        }
    }, [success]);

    const redirect = () => {
        if (onSavePressed) {
            history.push('/contents/lms/courses');
        }

        if (onNextPressed) {
            history.push(`/contents/lms/courses/${id || course.id}/lessons`);
        }
    };

    const disableSaveAndNavButtons = (disabled: boolean): void => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: disabled,
                nextDisabled: disabled,
            } as FloatingCourseActionsBarConfiguration),
        );
    };

    useEffect(() => {
        if (hasErrorOnUpdateScorm && originalCourse && course.id && course.scormId !== null && course.scormId !== undefined) {
            handleOnChange('scormPackageName', originalCourse.scormPackageName);
        }
    }, [hasErrorOnUpdateScorm]);

    const isScormInProcess = () =>
        course.id !== undefined &&
        course.id !== 0 &&
        course.scormId === null &&
        course.scormPackageName !== null &&
        course.scormPackageName !== undefined &&
        (!scormPackageStatus || scormPackageStatus.status === Constants.scormStatus.PROCESSING);

    const getRemoveFileLinkText = (): string => {
        if (course.id) {
            return isScormInProcess() ? strings.SCORM_PACKAGE_IN_PROCESS : strings.UPDATE_PACKAGE;
        } else {
            return strings.REMOVE_PACKAGE;
        }
    };

    const onScormUploadComplete = (scormPackageName: string): void => {
        handleOnChange('scormPackageName', scormPackageName);
        disableSaveAndNavButtons(!formIsValid);
        setOnUpload(false);
        if (course.id) {
            dispatch(updateScormPackageRequest({ courseId: course.id, scormPath: scormPackageName, scormId: course.scormId }));

            if (!course.scormId) {
                startCheckScormStatusInterval();
            }
        }
    };

    useEffect(() => {
        if (scormPackageStatus && scormPackageStatus.status !== Constants.scormStatus.PROCESSING) {
            clearCheckScormStatusInterval();
        }
    }, [scormPackageStatus]);

    const clearCheckScormStatusInterval = () => {
        if (checkScormStatusInterval) {
            clearInterval(checkScormStatusInterval as NodeJS.Timeout);
            setCheckScormStatusInterval(undefined);
        }
    };

    const startCheckScormStatusInterval = () => {
        if (checkScormStatusInterval === undefined && course.id && !onNextPressed && !onSavePressed) {
            setCheckScormStatusInterval(
                setInterval(() => {
                    dispatch(getScormPackageStatusRequest({ courseId: course.id, showToast: false }));
                }, 3000),
            );
        }
    };

    useEffect(() => {
        return () => {
            clearCheckScormStatusInterval();
        };
    }, [checkScormStatusInterval]);

    return (
        <div className="create-course__info position-relative">
            <ConfirmDialog
                content={confirmDialogState.scormAlert}
                leftButtonTitle={confirmDialogState.leftButtonTitle}
                leftClick={confirmDialogState.leftClick}
                rightButtonTitle={confirmDialogState.rightButtonTitle}
                rightClick={confirmDialogState.rightClick}
                show={show}
            />

            <FamilySelectionBox disabled={!!course.id} familyId={families[0]?.id} handleOnChange={handleOnChange} />

            <span className="create-program-info__warning">* {strings.REQUIRED_FIELDS}</span>
            <div className="input-section required">
                <h1 className="warning">{strings.HEADER}</h1>

                <Upload
                    onComplete={(response: FileUploadData) => handleOnChange('mainImage', response.key)}
                    onRemove={() => handleOnChange('mainImage', null)}
                    previewFile={{ uri: mainImage, name: mainImage }}
                    ref={uploadMainImageComponentRef}
                    typeAccepted="IMAGE"
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.TITLE}</h1>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('name', e.target.value)}
                    placeholder={strings.TYPE_TITLE}
                    type="text"
                    value={name}
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.AUTHOR}</h1>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('author', e.target.value)}
                    placeholder={strings.TYPE_AUTHOR}
                    type="text"
                    value={author}
                />
            </div>
            <div className="input-section">
                <h1>{strings.COURSE_DESCRIPTION}</h1>
                <textarea
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleOnChange('description', e.target.value)}
                    placeholder={strings.INSERT_DESCRIPTION_TO_HELP}
                    rows={3}
                    value={description}
                />
            </div>

            <div className="input-section required">
                <h1 className="warning">{strings.SELECT_LANGUAGE}</h1>
                <div className="container button-group-content-type">
                    <button
                        className={`btn btn-light ${locale === 'pt' ? 'active' : ''}`}
                        disabled={course.id ? true : false}
                        onClick={() => handleOnChange('locale', 'pt')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_PT}</span>
                    </button>

                    <button
                        className={`btn btn-light ${locale === 'en' ? 'active' : ''}`}
                        disabled={course.id ? true : false}
                        onClick={() => handleOnChange('locale', 'en')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_EN}</span>
                    </button>
                </div>
            </div>

            {(!course.id || (originalCourse && originalCourse.scormPackageName)) && (
                <div className="input-section">
                    <h1>
                        {strings.SCORM_PACKAGE}
                        <span className="scorm-hint">
                            <OverlayTrigger overlay={<Tooltip id="scorm-hint">{strings.SCORM_PACKAGE_ALERT}</Tooltip>} placement="bottom">
                                <i className="material-icons-outlined">{Constants.icons.ERROR_OUTLINE}</i>
                            </OverlayTrigger>
                        </span>
                    </h1>

                    <Upload
                        disabled={isScormInProcess()}
                        onCancel={() => {
                            setOnUpload(false);
                            disableSaveAndNavButtons(!formIsValid);
                        }}
                        onComplete={(response: FileUploadData) => onScormUploadComplete(response.key)}
                        onRemove={() => {
                            handleOnChange('scormPackageName', null);
                        }}
                        onRemoveFileLinkClick={
                            course.id
                                ? () => {
                                      setConfirmDialogState(updateScormPackageDialog);
                                      setShow(true);
                                  }
                                : undefined
                        }
                        onStart={() => {
                            setOnUpload(true);
                            disableSaveAndNavButtons(true);
                        }}
                        previewFile={{ name: scormPackageName || '', uri: scormPackageName || '' }}
                        ref={uploadScormImageComponentRef}
                        removeFileLinkText={getRemoveFileLinkText()}
                        typeAccepted="ZIP"
                    />
                </div>
            )}
            <div className="input-section required">
                <SelectCategoryForm categoriesForm={categories} familyId={families[0]?.id} handleOnChange={handleOnChange} />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.TAGS}</h1>
                <div className="tags">
                    <TagsInput selectedTags={tags} setTags={handleOnChange} />
                </div>
            </div>
        </div>
    );
};

export default CourseInfo;
